import * as React from 'react';
import AuditabilityTest from './AuditabilityTest';
import { generateString } from '../../pages/subpages/Dashboard';
import { cn } from '../../lib/utils';

// Count all tests recursively
const countTests = (group: any): { passed: number; failed: number } => {
    let passed = 0;
    let failed = 0;

    // Count checks in current group
    group.checks.forEach((test: any) => {
        if (test.is_valid) passed++;
        else failed++;
    });

    // Recursively count subgroups
    group.subgroups.forEach((subgroup: any) => {
        const subCounts = countTests(subgroup);
        passed += subCounts.passed;
        failed += subCounts.failed;
    });

    return { passed, failed };
};

export default function AuditabilityGroup({ group, identifier, isNested = false }) {
    const [expanded, setExpanded] = React.useState(false);

    const are_all_test_valids = (current_group: any): boolean => {
        return current_group.subgroups.filter((gr: any) => (!are_all_test_valids(gr))).length === 0 &&
            current_group.checks.filter((test: any) => !test.is_valid).length === 0;
    };

    const is_valid = are_all_test_valids(group);
    const { passed, failed } = countTests(group);
    const total = passed + failed;

    return (
        <div
            key={identifier}
            className={cn(
                "border rounded-lg bg-white overflow-hidden",
                isNested ? "border-slate-100 mb-2" : "border-slate-200 shadow-sm mb-3"
            )}
        >
            <button
                className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left",
                    is_valid ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-red-500"
                )}
                onClick={() => setExpanded(!expanded)}
            >
                {/* Status icon */}
                {is_valid ? (
                    <span className="material-symbols-outlined text-[20px] text-emerald-600 flex-shrink-0">check_circle</span>
                ) : (
                    <span className="material-symbols-outlined text-[20px] text-red-600 flex-shrink-0">cancel</span>
                )}

                {/* Group name */}
                <span className="flex-1 font-medium text-slate-900 truncate">
                    {group.name}
                </span>

                {/* Test counts */}
                {total > 0 && (
                    <div className="flex items-center gap-2 text-xs flex-shrink-0">
                        {passed > 0 && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                                <span className="material-symbols-outlined text-[14px]">check</span>
                                {passed}
                            </span>
                        )}
                        {failed > 0 && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                                <span className="material-symbols-outlined text-[14px]">close</span>
                                {failed}
                            </span>
                        )}
                    </div>
                )}

                {/* Expand icon */}
                <span className={cn(
                    "material-symbols-outlined text-[20px] text-slate-400 transition-transform duration-200 flex-shrink-0",
                    expanded ? "rotate-180" : ""
                )}>expand_more</span>
            </button>

            {expanded && (
                <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50/50">
                    <div className="space-y-2">
                        {group.subgroups.map((sub_group: any) => (
                            <AuditabilityGroup
                                key={generateString(10)}
                                group={sub_group}
                                identifier={generateString(10)}
                                isNested={true}
                            />
                        ))}
                        {group.checks.map((test: any) => (
                            <AuditabilityTest
                                key={generateString(10)}
                                test={test}
                                identifier={generateString(10)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
